/**
 * HARDLOCK v18.12.71-STABLE-HYDRATION-FIX
 * Archivo critico de Planeacion/Seguimiento.
 * No modificar sin snapshot, pruebas hardlock, localhost validation y revision de no regresion.
 */
import { PLANNING_SECTION_CONTRACT } from './planningConstants';
import type { SectionNumber } from './planningTypes';
import { normalizeStr, resolveSectionNumberSafe } from './planningGuards';

const sanitizeRow = (row: any, sn: number): any => {
    if (!row || typeof row !== 'object') return row;
    
    // v18.12.41.1-COUNTING-RESTORE-STABLE: No eliminar columnas.
    // El engine contará lo que tenga valor significativo,
    // pero no debemos mutar/eliminar propiedades de los payloads dinámicos
    // porque rompe la persistencia e interfaz de columnas.
    return { ...row };
};

export const normalizeSectionContent = (sn: number, rawContent: any): any => {
    if (!rawContent) return {};
    
    let contentObj = rawContent;
    if (typeof rawContent === 'string') {
        try {
            contentObj = JSON.parse(rawContent);
        } catch (e) {
            console.error(`[PlanningEngine] Failed to parse content for S${sn}`, e);
            return {};
        }
    }
    
    if (typeof contentObj !== 'object') return {};

    const newContent = { ...contentObj };
    const contract = PLANNING_SECTION_CONTRACT[sn as unknown as SectionNumber];
    const canonicalKey = contract ? contract.canonicalKey : `sec${sn}_data`;

    // v18.12.40.1: BLOQUEO DE CONTAMINACIÓN CRUZADA (Anti-cross-section guardrails)
    const aliases: Record<number, string[]> = {
        1: ['sec1_data', 'objetivos_data', 'contribucion_agenda'],
        2: ['sec2_data', 'objetivos_presidente_data', 'plan_estrategico'],
        3: ['sec3_data', 'diagnostico_data', 'swot_sector', 'foda'],
        4: ['sec4_data', 'estructura_data'],
        5: ['sec5_data', 'comisiones_data', 'vinculacion'],
        6: ['sec6_data', 'eventos_data'],
        7: ['sec7_data', 'proyectos_data', 'projects'],
        8: ['sec8_data', 'convenios_data', 'convenios'],
        9: ['sec9_data', 'temas_data', 'representacion']
    };

    if (!newContent[canonicalKey]) {
        const fallbacks = aliases[sn] || [];
        for (const key of fallbacks) {
            if (newContent[key] && (Array.isArray(newContent[key]) || typeof newContent[key] === 'object')) {
                newContent[canonicalKey] = newContent[key];
                break;
            }
        }
    }

    if (Array.isArray(newContent[canonicalKey])) {
        newContent[canonicalKey] = newContent[canonicalKey].map((r: any) => sanitizeRow(r, sn));
    }

    // v18.12.45: Eliminamos el mapeo interno de S3 aquí para que el handler (que es omnívoro) 
    // maneje directamente los nombres de campos legacy o modernos sin colisiones.

    // v18.12.40.2: ANTI-CROSS-SECTION HARDLOCK (Solo devolvemos lo que pertenece a la sección)
    // v18.12.50: Excepción para S3 que usa un objeto raíz plano en muchos casos legacy.
    const filteredContent: any = {};
    if (sn === 3) {
        return newContent; // Preservar todo para diagnóstico
    }

    if (newContent[canonicalKey]) filteredContent[canonicalKey] = newContent[canonicalKey];
    Object.keys(newContent).forEach(k => {
        if (k.startsWith('_config_')) filteredContent[k] = newContent[k];
    });

    return filteredContent;
};

export const resolveCanonicalSectionNumber = (section: any): number | null => {
    if (!section) return null;

    // v18.12.41: PRIORIDAD 1 - section_number real del backend (Safe resolution)
    const snFromDb = resolveSectionNumberSafe(section.section_number);
    if (snFromDb !== null) return snFromDb;

    // v18.12.40.1: PRIORIDAD 2 - Type del contrato
    const type = section.type || "";
    for (const [sn, contract] of Object.entries(PLANNING_SECTION_CONTRACT)) {
        if (contract.type === type) return Number(sn);
    }

    // v18.12.40.1: PRIORIDAD 3 - Canonical Key en el contenido
    let contentKeys: string[] = [];
    try {
        const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : (section.content || {});
        contentKeys = Object.keys(parsed);
    } catch(e) {}
    
    for (const [sn, contract] of Object.entries(PLANNING_SECTION_CONTRACT)) {
        if (contentKeys.includes(contract.canonicalKey)) return Number(sn);
    }

    // v18.12.40.1: PRIORIDAD 4 - Fallback textual inteligente
    const name = normalizeStr(section.name || section.section_name || "");
    if (name.includes("OBJETIVOS DE CONTRIBUCION") || name.includes("OBJETIVOS DE LA AGENDA") || name.includes("CONTRIBUCION A LA AGENDA")) return 1;
    if (name.includes("OBJETIVOS DEL PLAN") || name.includes("PLAN ESTRATEGICO") || name.includes("OBJETIVOS ESTRATEGICOS") || name.includes("OBJETIVOS DEL PRESIDENTE")) return 2;
    if (name.includes("DIAGNOSTICO")) return 3;
    if (name.includes("ESTRUCTURA")) return 4;
    if (name.includes("COMISIONES LOCALES") || name.includes("TRABAJO CONJUNTO") || name.includes("VINCULACION") || name.includes("CREACION DE COMISIONES")) return 5;
    if (name.includes("EVENTO") || name.includes("ASISTENTES")) return 6;
    if (name.includes("PROYECTO")) return 7;
    if (name.includes("CONVENIO")) return 8;
    if (name.includes("TEMAS RELEVANTES") || name.includes("TEMAS EN LOS QUE") || name.includes("REPRESENTACION")) return 9;

    return null;
};
